<!-- eslint-disable vue/first-attribute-linebreak -->
<!-- Customer Behavior — when customers come back to a ticket relative to its due date (ticket age
     before it, on it, late, postponed), compared month / quarter / year between two periods A and B.
     Data: POST /score-svc/insight/due-behavior on the Credit-Score-Service (monthly counts, via
     insightApi.dueBehavior); quarters and years are rolled up here. Same page as pawnshop-admin-new. -->
<template>
  <div class="container-fluid cb-page">
    <div v-if="loading" class="loading-state">
      <ProgressSpinner fill="transparent" aria-label="Loading" />
    </div>

    <!-- Filters + summary -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="card mt-2">
          <div class="card-header mb-0 pb-0">
            <h5 class="mb-1">วิเคราะห์พฤติกรรมลูกค้า</h5>
            <p class="text-sm mb-0">Customer Behavior Analysis · จังหวะการทำรายการเทียบกับวันครบกำหนด</p>
          </div>
          <div class="card-body pt-3">
            <div class="cb-controls">
              <div class="cb-ctl">
                <label class="form-label ms-0">มุมมอง</label>
                <div class="cb-seg" role="group" aria-label="มุมมองช่วงเวลา">
                  <button v-for="g in GRANS" :key="g.v" type="button" class="btn btn-sm mb-0"
                    :class="gran === g.v ? 'btn-primary' : 'btn-outline-primary'" :aria-pressed="gran === g.v"
                    @click="setGran(g.v)">{{ g.lb }}</button>
                </div>
              </div>
              <div class="cb-ctl">
                <label class="form-label ms-0">ประเภทธุรกรรม</label>
                <div class="cb-seg" role="group" aria-label="ประเภทธุรกรรม">
                  <button v-for="o in TYPE_FILTERS" :key="o.v" type="button" class="btn btn-sm mb-0"
                    :class="typeFilter === o.v ? 'btn-primary' : 'btn-outline-primary'" :aria-pressed="typeFilter === o.v"
                    @click="typeFilter = o.v">{{ o.lb }}</button>
                </div>
              </div>
              <div class="cb-ctl cb-ctl-wide">
                <label class="form-label ms-0">เทียบช่วง</label>
                <div class="cb-pair">
                  <span class="cb-tag a" aria-hidden="true">A</span>
                  <PeriodPicker v-model="periodA" slot-name="A" :gran="gran" :periods="periodList"
                    :other-value="periodB" :label-of="label" :is-warm="isWarm" />
                  <span class="text-sm">กับ</span>
                  <span class="cb-tag b" aria-hidden="true">B</span>
                  <PeriodPicker v-model="periodB" slot-name="B" :gran="gran" :periods="periodList"
                    :other-value="periodA" :label-of="label" :is-warm="isWarm" allow-clear />
                </div>
              </div>
            </div>

            <p v-if="errorMsg" class="text-sm text-danger mt-3 mb-0">{{ errorMsg }}</p>
            <hr />

            <div v-if="!hasData && !loading" class="cb-empty">
              <i class="material-icons material-symbols-outlined" aria-hidden="true">upload_file</i>
              <p class="mb-0">ยังไม่มีข้อมูลธุรกรรมของร้านนี้ อัปโหลดไฟล์ที่หน้า {{ $t("apps.csupload") }} ก่อน แล้วกลับมาที่หน้านี้</p>
            </div>

            <template v-else-if="hasData">
              <div class="cb-section-head">
                <h6 class="mb-0">สรุปช่วงที่เลือก</h6>
                <span class="text-sm" aria-live="polite">{{ selectionText }}</span>
              </div>
              <div class="cb-tiles">
                <div v-for="t in tiles" :key="t.label" class="stat-tile" :style="{ borderTopColor: t.accent }">
                  <span class="stat-label">{{ t.label }}</span>
                  <div class="stat-ico" :style="{ background: t.tint, color: t.accent }">
                    <i class="material-icons material-symbols-outlined" aria-hidden="true">{{ t.icon }}</i>
                  </div>
                  <span class="stat-value">{{ t.value }}</span>
                  <span class="stat-delta">
                    <span v-if="t.split" class="split">{{ t.split }}</span>
                    <span v-if="t.cmp" class="cmp">{{ t.cmp }}</span>
                    <span v-if="t.delta" class="chg" :class="t.dir">{{ t.delta }}</span>
                  </span>
                </div>
              </div>
              <div v-if="howTo" class="cb-howto">
                <i class="material-icons material-symbols-outlined" aria-hidden="true">help_outline</i>
                <div>
                  <p class="cb-howto-title">วิธีอ่านตัวเลขเทียบกับ {{ howTo.b }}</p>
                  <ul>
                    <li><b>รายการทั้งหมด</b> เทียบ<b>จำนวนรายการ</b>เป็นเปอร์เซ็นต์: {{ howTo.total }}</li>
                    <li><b>ก่อน / วันครบ / เกิน due date</b> เป็น<b>สัดส่วน</b>ของรายการอยู่แล้ว จึงเทียบด้วยการ<b>ลบกันตรงๆ</b>: {{ howTo.share }}</li>
                  </ul>
                </div>
              </div>
              <div v-if="flags.length" class="cb-flag">
                <i class="material-icons material-symbols-outlined" aria-hidden="true">info</i>
                <div><div v-for="f in flags" :key="f.tag"><b>{{ f.tag }} · {{ f.period }}</b>: {{ f.text }}</div></div>
              </div>
              <div class="cb-insight">
                <i class="material-icons material-symbols-outlined" aria-hidden="true">insights</i>
                <div>
                  <p class="cb-insight-label">อ่านภาพรวม · ทุกประเภทธุรกรรม</p>
                  <!-- eslint-disable-next-line vue/no-v-html -->
                  <p class="text-sm mb-0" v-html="ledeHtml"></p>
                </div>
              </div>
              <p class="text-xs text-secondary mt-3 mb-0">{{ metaText }}</p>
            </template>
          </div>
        </div>
      </div>
    </div>

    <template v-if="hasData">
      <!-- Charts -->
      <div class="row">
        <div class="col-xxl-7 mb-4">
          <div class="card h-100">
            <div class="card-header pb-0 cb-card-head">
              <div>
                <h6 class="mb-0">{{ trendTitle }}</h6>
                <p class="text-sm mb-0">ติดตามสัดส่วนพฤติกรรมที่เปลี่ยนไปในแต่ละช่วงเวลา</p>
              </div>
              <div class="cb-seg" role="group" aria-label="หน่วย">
                <button type="button" class="btn btn-sm mb-0" :class="measure === 'pct' ? 'btn-primary' : 'btn-outline-primary'"
                  :aria-pressed="measure === 'pct'" @click="measure = 'pct'">สัดส่วน %</button>
                <button type="button" class="btn btn-sm mb-0" :class="measure === 'cnt' ? 'btn-primary' : 'btn-outline-primary'"
                  :aria-pressed="measure === 'cnt'" @click="measure = 'cnt'">จำนวนรายการ</button>
              </div>
            </div>
            <div class="card-body pt-3">
              <div class="cb-legend">
                <span v-for="b in BUCKETS" :key="b.name"><span class="cb-sw" :style="{ background: b.color }"></span>{{ b.name }}</span>
              </div>
              <div ref="trendBox" class="cb-chart-scroll" tabindex="0" role="region"
                aria-label="กราฟจังหวะการมาทำรายการ เลื่อนแนวนอนเพื่อดูทุกช่วงเวลา" @scroll.passive="trendTouched = true">
                <div class="cb-chart" :style="{ width: trend.W + 'px' }">
                  <svg :width="trend.W" :height="trend.H" role="group"
                    :aria-label="`แผนภูมิแท่งซ้อน${measure === 'pct' ? 'สัดส่วน' : 'จำนวน'}จังหวะการมาทำรายการ เลือกแท่งด้วย Enter`">
                    <g v-for="t in trend.ticks" :key="'t' + t.v">
                      <line :x1="trend.L" :x2="trend.W - trend.R" :y1="t.y" :y2="t.y" stroke="#eceef1" />
                      <text :x="trend.L - 8" :y="t.y + 4" text-anchor="end" font-size="11.5" fill="#7b809a">{{ t.label }}</text>
                    </g>
                    <g v-for="(b, i) in trend.bars" :key="b.k">
                      <rect v-if="b.tag" :x="b.x0 + 2" :y="trend.Tp - 26" :width="trend.step - 4" :height="trend.Hp + 26" rx="6" fill="rgba(5,175,80,.08)" />
                      <g :opacity="b.warm ? 0.4 : 1">
                        <path v-for="s in b.segs" :key="s.j" :d="s.d" :fill="s.fill" />
                      </g>
                      <template v-if="b.tag">
                        <rect :x="b.cx - 10" :y="trend.Tp - 22" width="20" height="18" rx="4" :fill="b.tag === 'A' ? '#05af50' : '#ced4da'" />
                        <text :x="b.cx" :y="trend.Tp - 9" text-anchor="middle" font-size="11.5" font-weight="700"
                          :fill="b.tag === 'A' ? '#fff' : '#344767'">{{ b.tag }}</text>
                      </template>
                      <text v-if="b.lateLabel" :x="b.cx + trend.bw / 2 + 4" :y="b.lateY" font-size="11.5" font-weight="700" fill="#344767">{{ b.lateLabel }}</text>
                      <text :x="b.cx" :y="trend.Tp + trend.Hp + 18" text-anchor="middle" font-size="12" fill="#67748e">{{ b.l1 }}</text>
                      <text v-if="b.l2" :x="b.cx" :y="trend.Tp + trend.Hp + 34" text-anchor="middle" font-size="11" fill="#7b809a">{{ b.l2 }}</text>
                      <rect :x="b.x0" :y="trend.Tp - 26" :width="trend.step" :height="trend.Hp + 26 + trend.Bt" fill="transparent"
                        style="cursor: pointer" tabindex="0" role="button" :aria-label="`เลือก ${label(b.k, true)} เป็นช่วง A`"
                        :aria-pressed="b.k === periodA" @mouseenter="hover = i" @mouseleave="hover = null" @focus="hover = i"
                        @blur="hover = null" @click="setA(b.k)" @keydown.enter.prevent="setA(b.k)" @keydown.space.prevent="setA(b.k)" />
                    </g>
                    <line :x1="trend.L" :x2="trend.W - trend.R" :y1="trend.Tp + trend.Hp" :y2="trend.Tp + trend.Hp" stroke="#7b809a" />
                  </svg>
                  <div v-if="hoverBar" class="cb-tip" :style="hoverBar.tipStyle">
                    <div class="cb-tip-title">{{ label(hoverBar.k, true) }} · {{ fmt(hoverBar.n) }} รายการ</div>
                    <div v-for="r in hoverBar.tipRows" :key="r.name" class="cb-tip-row">
                      <span class="cb-sw" :style="{ background: r.color }"></span>{{ r.name }}
                      <span class="cb-tip-v">{{ p2(r.pct) }} · {{ fmt(r.n) }}</span>
                    </div>
                    <div v-if="hoverBar.note" class="text-xs text-secondary mt-1">{{ hoverBar.note }}</div>
                    <div class="text-xs text-secondary">คลิกเพื่อตั้งเป็นช่วง A</div>
                  </div>
                </div>
              </div>
              <p class="cb-footnote">คลิกแท่งเพื่อตั้งเป็นช่วง A · แท่งสีจางคือช่วงตั้งต้นที่ตั๋วส่วนใหญ่ยังไม่ครบกำหนด</p>
            </div>
          </div>
        </div>

        <div class="col-xxl-5 mb-4">
          <div class="card h-100">
            <div class="card-header pb-0 cb-card-head">
              <div>
                <h6 class="mb-0">มาทำรายการตอนตั๋วอายุเท่าไร หรือเกินกำหนดกี่วัน</h6>
                <p class="text-sm mb-0">เปรียบเทียบการกระจายตัวของช่วง A และ B</p>
              </div>
              <button type="button" class="btn btn-sm btn-outline-primary mb-0 cb-toggle-btn" :aria-expanded="open.dist"
                @click="open.dist = !open.dist">
                <i class="material-icons material-symbols-outlined" aria-hidden="true">{{ open.dist ? "expand_less" : "expand_more" }}</i>
                {{ open.dist ? "ย่อเป็นยอดรวมก่อนกำหนด" : "ดูตามอายุตั๋ว" }}
              </button>
            </div>
            <div class="card-body pt-3">
              <div class="cb-legend">
                <span><span class="cb-tag a" aria-hidden="true">A</span>{{ label(periodA, true) }} (แท่งสี)</span>
                <span><span class="cb-tag b" aria-hidden="true">B</span>{{ periodB ? label(periodB, true) : "—" }} (แท่งเทา)</span>
              </div>
              <div ref="distBox" class="cb-chart-scroll">
                <svg :width="dist.W" :height="dist.H" role="img"
                  aria-label="แผนภูมิแท่งแนวนอน สัดส่วนรายการตามอายุตั๋วหรือจำนวนวันที่เกินกำหนด ช่วง A และ B">
                  <line v-for="g in dist.grid" :key="'g' + g" :x1="g" :x2="g" :y1="dist.Tp" :y2="dist.H - 4" stroke="#eceef1" />
                  <g v-for="r in dist.rows" :key="r.name">
                    <line v-if="r.sep" x1="0" :x2="dist.W" :y1="r.y0 - 3" :y2="r.y0 - 3" stroke="#dee2e6" />
                    <text x="0" :y="r.y0 + dist.rowH / 2 + 4" font-size="12.5" fill="#67748e">{{ r.name }}</text>
                    <path v-if="r.dA" :d="r.dA" :fill="r.color" />
                    <text :x="r.xA + 6" :y="r.y0 + 4 + dist.bh - 1" font-size="11.5" font-weight="700" fill="#344767">{{ p2(r.va) }}</text>
                    <template v-if="r.vb !== null">
                      <path v-if="r.dB" :d="r.dB" fill="#ced4da" />
                      <text :x="r.xB + 6" :y="r.y0 + 4 + 2 * dist.bh + 1" font-size="11" fill="#7b809a">{{ p2(r.vb) }}</text>
                    </template>
                  </g>
                </svg>
              </div>
              <p class="cb-footnote">อายุตั๋วนับแบบวันชนวันจากวันออกตั๋ว ตามขั้นการคิดดอกเบี้ย · ตามตัวกรองประเภทธุรกรรมที่เลือก</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Table: each transaction type, when it happens (columns sum to 100%) -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card">
            <div class="card-header pb-0 cb-card-head">
              <div>
                <h6 class="mb-0">แต่ละธุรกรรม มาทำรายการเมื่อไร</h6>
                <p class="text-sm mb-0">อ่านตามคอลัมน์: รายการของธุรกรรมแต่ละประเภทเกิดตอนตั๋วอายุเท่าไร ตรงวันครบ หรือเกินกำหนดกี่เปอร์เซ็นต์ · แต่ละคอลัมน์รวมเป็น 100%</p>
              </div>
              <span class="cb-badge">ทุกประเภทธุรกรรม</span>
            </div>
            <div class="card-body pt-3">
              <div class="cb-table-context">
                <span><span class="cb-tag a" aria-hidden="true">A</span><b>ช่วงหลัก:</b> {{ label(periodA, true) }}</span>
                <span><span class="cb-tag b" aria-hidden="true">B</span><b>ช่วงเทียบ:</b> {{ periodB ? label(periodB, true) : "ไม่ได้เลือกช่วงเทียบ" }}</span>
                <span class="text-secondary">ผลต่าง = % ช่วงหลัก ลบ % ช่วงเทียบ ตรงๆ เช่น 64.48% − 65.21% = −0.73</span>
              </div>
              <div class="table-responsive cb-cmp-scroll" tabindex="0" role="region" aria-label="ตารางจังหวะการทำรายการ เลื่อนแนวนอนเพื่อดูทุกคอลัมน์">
                <table class="table mb-0 cb-cmp" style="min-width: 1400px">
                  <thead>
                    <tr class="grp">
                      <th rowspan="2" scope="col" class="cb-rowhead">กลุ่มเวลาทำรายการ</th>
                      <th v-for="c in timing.cols" :key="c.n" colspan="3" scope="colgroup" class="group-start">{{ c.n }}</th>
                    </tr>
                    <tr class="subhead">
                      <template v-for="c in timing.cols" :key="'s' + c.n">
                        <th class="group-start"><span class="th-main">สัดส่วนช่วงหลัก</span><span class="th-sub">{{ label(periodA, true) }}</span></th>
                        <th><span class="th-main">สัดส่วนช่วงเทียบ</span><span class="th-sub">{{ periodB ? label(periodB, true) : "—" }}</span></th>
                        <th><span class="th-main">ผลต่าง</span><span class="th-sub">% ของ A ลบ % ของ B</span></th>
                      </template>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="r in timing.rows" v-show="r.cls !== 'sub' || open.timing" :key="r.name" :class="r.cls"
                      @click="r.cls === 'subtotal' && (open.timing = !open.timing)">
                      <th scope="row">
                        <span class="cb-rowlabel">
                          <button v-if="r.cls === 'subtotal'" type="button" class="cb-row-toggle" :aria-expanded="open.timing"
                            :aria-label="`${open.timing ? 'ซ่อน' : 'แสดง'}ช่วงอายุตั๋ว`" @click.stop="open.timing = !open.timing">
                            <i class="material-icons material-symbols-outlined" aria-hidden="true">chevron_right</i>
                          </button>
                          <span v-if="r.color" class="cb-sw" :style="{ background: r.color }"></span>{{ r.name }}
                          <span v-if="r.cls === 'subtotal'" class="cb-row-hint">{{ open.timing ? "ซ่อนรายละเอียด" : "ดูตามอายุตั๋ว" }}</span>
                        </span>
                      </th>
                      <template v-for="(c, ci) in r.cells" :key="ci">
                        <td class="group-start">{{ p2(c.pa) }}</td>
                        <td class="b">{{ c.pb === null ? "—" : p2(c.pb) }}</td>
                        <td class="d" :class="toneOf(c.pa, c.pb)">{{ c.pb === null ? "—" : deltaPp(c.pa, c.pb) }}</td>
                      </template>
                    </tr>
                    <tr class="total">
                      <th scope="row">จำนวนรายการ (100%)</th>
                      <template v-for="(c, ci) in timing.totals" :key="'n' + ci">
                        <td class="group-start">{{ fmt(c.nA) }}</td>
                        <td class="b">{{ c.nB === null ? "—" : fmt(c.nB) }}</td>
                        <td class="d">{{ c.chg === null ? "—" : pctChange(c.chg) }}</td>
                      </template>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Table: each timing group, which transaction types (rows sum to 100%) -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card">
            <div class="card-header pb-0 cb-card-head">
              <div>
                <h6 class="mb-0">แต่ละกลุ่มเป็นธุรกรรมอะไร</h6>
                <p class="text-sm mb-0">อ่านตามแถว: รายการในแต่ละกลุ่มเวลา (รวมแต่ละช่วงอายุตั๋ว) เป็นธุรกรรมประเภทใดบ้าง · แต่ละแถวรวมเป็น 100% ของกลุ่มนั้น</p>
              </div>
              <span class="cb-badge">ทุกประเภทธุรกรรม</span>
            </div>
            <div class="card-body pt-3">
              <div class="cb-table-context">
                <span><span class="cb-tag a" aria-hidden="true">A</span><b>ช่วงหลัก:</b> {{ label(periodA, true) }}</span>
                <span><span class="cb-tag b" aria-hidden="true">B</span><b>ช่วงเทียบ:</b> {{ periodB ? label(periodB, true) : "ไม่ได้เลือกช่วงเทียบ" }}</span>
                <span class="text-secondary">ผลต่าง = % ช่วงหลัก ลบ % ช่วงเทียบ ตรงๆ เช่น 64.48% − 65.21% = −0.73</span>
              </div>
              <div class="table-responsive cb-cmp-scroll" tabindex="0" role="region" aria-label="ตารางประเภทธุรกรรมในแต่ละกลุ่ม เลื่อนแนวนอนเพื่อดูทุกคอลัมน์">
                <table class="table mb-0 cb-cmp" style="min-width: 1600px">
                  <thead>
                    <tr class="grp">
                      <th rowspan="2" scope="col" class="cb-rowhead">กลุ่มเวลาทำรายการ</th>
                      <th v-for="n in TYPES" :key="n" colspan="3" scope="colgroup" class="group-start">ธุรกรรม{{ n }}</th>
                      <th colspan="2" scope="colgroup" class="group-start">เงินต้นเฉลี่ยต่อรายการ</th>
                      <th rowspan="2" scope="col" class="group-start"><span class="th-main">จำนวนรายการ</span><span class="th-sub">ช่วงหลัก</span></th>
                    </tr>
                    <tr class="subhead">
                      <template v-for="n in TYPES" :key="'s' + n">
                        <th class="group-start"><span class="th-main">สัดส่วนช่วงหลัก</span><span class="th-sub">{{ label(periodA, true) }}</span></th>
                        <th><span class="th-main">สัดส่วนช่วงเทียบ</span><span class="th-sub">{{ periodB ? label(periodB, true) : "—" }}</span></th>
                        <th><span class="th-main">ผลต่าง</span><span class="th-sub">% ของ A ลบ % ของ B</span></th>
                      </template>
                      <th class="group-start"><span class="th-main">ค่าเฉลี่ยช่วงหลัก</span><span class="th-sub">บาท</span></th>
                      <th><span class="th-main">ค่าเฉลี่ยช่วงเทียบ</span><span class="th-sub">บาท</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="r in mix" v-show="r.cls !== 'sub' || open.mix" :key="r.name" :class="r.cls"
                      @click="r.cls === 'subtotal' && (open.mix = !open.mix)">
                      <th scope="row">
                        <span class="cb-rowlabel">
                          <button v-if="r.cls === 'subtotal'" type="button" class="cb-row-toggle" :aria-expanded="open.mix"
                            :aria-label="`${open.mix ? 'ซ่อน' : 'แสดง'}ช่วงอายุตั๋ว`" @click.stop="open.mix = !open.mix">
                            <i class="material-icons material-symbols-outlined" aria-hidden="true">chevron_right</i>
                          </button>
                          <span v-if="r.color" class="cb-sw" :style="{ background: r.color }"></span>{{ r.name }}
                          <span v-if="r.cls === 'subtotal'" class="cb-row-hint">{{ open.mix ? "ซ่อนรายละเอียด" : "ดูตามอายุตั๋ว" }}</span>
                        </span>
                      </th>
                      <template v-for="(c, ci) in r.cells" :key="ci">
                        <td class="group-start"><span class="cb-cellbar">{{ p2(c.pa) }}<i :style="{ width: Math.round(c.pa * 0.5) + 'px' }"></i></span></td>
                        <td class="b">{{ c.pb === null ? "—" : p2(c.pb) }}</td>
                        <td class="d" :class="toneOf(c.pa, c.pb)">{{ c.pb === null ? "—" : deltaPp(c.pa, c.pb) }}</td>
                      </template>
                      <td class="group-start">{{ r.avgA === null ? "—" : fmt(r.avgA) }}</td>
                      <td class="b">{{ r.avgB === null ? "—" : fmt(r.avgB) }}</td>
                      <td class="group-start">{{ fmt(r.nA) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Table: every period, grouped by year -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card">
            <!-- collapsed by default: the whole table only renders after the header is clicked -->
            <div class="card-header cb-card-head" :class="showAll ? 'pb-0' : 'pb-3'">
              <button type="button" class="cb-section-toggle" :aria-expanded="showAll" aria-controls="cbAllBody"
                @click="toggleAll">
                <span class="cb-row-toggle" aria-hidden="true">
                  <i class="material-icons material-symbols-outlined">chevron_right</i>
                </span>
                <span>
                  <h6 class="mb-0">ข้อมูลทุกช่วงเวลา</h6>
                  <span class="text-sm d-block">{{ showAll ? allInfo + " · ตามมุมมองและตัวกรองที่เลือก" : "กดเพื่อแสดงตาราง" }}</span>
                </span>
              </button>
              <div v-if="showAll && gran !== 'year'" class="cb-seg">
                <button type="button" class="btn btn-sm btn-outline-primary mb-0" @click="openYears = new Set(yearList)">ขยายทุกปี</button>
                <button type="button" class="btn btn-sm btn-outline-primary mb-0" @click="openYears = new Set()">ย่อทุกปี</button>
              </div>
            </div>
            <div v-if="showAll" id="cbAllBody" class="card-body pt-3">
              <div class="cb-legend mb-2">
                <span v-for="b in BUCKETS" :key="b.name"><span class="cb-sw" :style="{ background: b.color }"></span>{{ b.name }}</span>
              </div>
              <div class="table-responsive cb-all-scroll" tabindex="0" role="region" aria-label="ตารางทุกช่วงเวลา">
                <table class="table mb-0 cb-all">
                  <thead>
                    <tr>
                      <th scope="col">ช่วง</th>
                      <th scope="col">รายการ</th>
                      <th scope="col">สัดส่วน</th>
                      <th v-for="b in BUCKETS" :key="b.short" scope="col"><span class="cb-sw" :style="{ background: b.color }"></span> {{ b.short }}</th>
                      <th scope="col">หมายเหตุ</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="r in allRows" v-show="!r.child || openYears.has(r.year)" :key="r.key" :class="{ 'year-row': r.isYear, child: r.child, 'is-a': r.key === periodA }"
                      @click="r.isYear && toggleYear(r.year)">
                      <th scope="row">
                        <button v-if="r.isYear" type="button" class="cb-row-toggle" :aria-expanded="openYears.has(r.year)"
                          :aria-label="`${openYears.has(r.year) ? 'ย่อ' : 'ขยาย'} ปี ${+r.year + 543}`" @click.stop="toggleYear(r.year)">
                          <i class="material-icons material-symbols-outlined" aria-hidden="true">chevron_right</i>
                        </button>
                        {{ r.name }}
                        <span v-if="r.count" class="cb-row-hint">{{ r.count }}</span>
                        <span v-for="tg in r.tags" :key="tg" class="cb-tag sm" :class="tg.toLowerCase()">{{ tg }}</span>
                      </th>
                      <td>{{ fmt(r.n) }}</td>
                      <td>
                        <div class="cb-mixbar" aria-hidden="true">
                          <span v-for="(v, j) in r.b" v-show="v > 0" :key="j" :style="{ flex: v + ' 1 0', background: BUCKETS[j].color }"></span>
                        </div>
                      </td>
                      <td v-for="(v, j) in r.b" :key="'v' + j">{{ p2(pct(v, r.n)) }}<small>{{ fmt(v) }}</small></td>
                      <td class="note">{{ r.note }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Definitions -->
      <div class="row mb-5">
        <div class="col-12">
          <div class="card">
            <div class="card-header pb-0"><h6 class="mb-0">นิยามที่ใช้ในหน้านี้</h6></div>
            <div class="card-body pt-3">
              <ul class="cb-notes text-sm">
                <li>นับทุกรายการที่ทำกับตั๋วเดิม คือ <b>ต่อดอก เพิ่มต้น ลดต้น ไถ่ถอน</b> แล้วเทียบวันที่ทำรายการกับวันครบกำหนดของตั๋วใบก่อนหน้า รายการจำนำใหม่ไม่ถูกนับ เพราะไม่มีตั๋วเดิม และรายการแรกของตั๋วที่ย้ายมาจากระบบเก่าก็ไม่ถูกนับ เพราะไม่มีข้อมูลตั๋วใบก่อนในระบบ</li>
                <li><b>อายุตั๋ว</b> (ใช้แบ่งรายการที่มาก่อนวันครบกำหนด) นับแบบวันชนวันจากวันออกตั๋ว ตามขั้นการคิดดอกเบี้ย: วันที่ 1–16 = 0.5 เดือน, วันที่ 17 ถึงวันชนวัน = 1 เดือน, เลยวันชนวัน 1–15 วัน = 1.5 เดือน แล้วเพิ่มทีละครึ่งเดือนไปจนถึง 6 เดือน ซึ่งคือช่วงครึ่งเดือนสุดท้ายก่อนวันครบกำหนด</li>
                <li><b>ตั๋วที่ถูกผลัด</b> นับเป็น <b>เกินกำหนด</b> ทุกกรณี และแยกแสดงเป็นกลุ่ม “ผลัดตั๋ว” ตั๋วที่ไม่ได้ผลัดเทียบกับวันครบกำหนดเดิม</li>
                <li>ตั๋วที่ไม่มีใครมาทำรายการจนกลายเป็นทรัพย์หลุด ไม่อยู่ในตัวเลขนี้ เพราะไม่มีรายการให้นับ</li>
                <li><b>ช่วงตั้งต้น:</b> ตั๋วมีอายุ 6 เดือน ช่วง 6 เดือนแรกนับจากวันแรกของข้อมูลจึงมีแต่รายการที่ทำก่อนวันครบกำหนด ใช้เทียบกับช่วงหลังไม่ได้ ({{ warmText }})</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted, onBeforeUnmount, nextTick } from "vue";
import { useI18n } from "vue-i18n";
import ProgressSpinner from "primevue/progressspinner";
import PeriodPicker from "./CustomerBehaviorPeriodPicker.vue";
import { useInsightStore } from "@/stores/insight";

useI18n();
const store = useInsightStore();

// ---------- constants ----------
const API_TYPES = ["RENEW", "INCREASE", "REDUCE", "REDEEM"];
const TYPES = ["ต่อดอก", "เพิ่มต้น", "ลดต้น", "ไถ่ถอน"];
const TYPE_FILTERS = [{ v: "all", lb: "ทั้งหมด" }, ...TYPES.map((lb, i) => ({ v: String(i), lb }))];
const GRANS = [{ v: "month", lb: "รายเดือน" }, { v: "quarter", lb: "รายไตรมาส" }, { v: "year", lb: "รายปี" }];
// timing buckets — Tailwind 600/500 steps, adjacent pairs checked for colour-blind separation
const BUCKETS = [
  { name: "ก่อน due", short: "ก่อน due", color: "#2563eb" },
  { name: "วันครบ due", short: "วันครบ", color: "#0d9488" },
  { name: "เกิน due (ไม่ได้ผลัด)", short: "เกิน (ไม่ผลัด)", color: "#f59e0b" },
  { name: "เกิน due (ผลัดตั๋ว)", short: "ผลัดตั๋ว", color: "#7c3aed" },
];
// bin codes from /insight/due-behavior: 0..11 = ticket age 0.5..6 months, 12 = due date,
// 13–15 = 1–7 / 8–30 / >30 days late, 16 = postponed
const AGES = [0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6];
const BINS = [
  ...AGES.map((m) => ({ name: `ตั๋วอายุ ${m} เดือน`, b: 0 })),
  { name: "ตรงวันครบกำหนด", b: 1 },
  { name: "เกิน 1–7 วัน", b: 2 }, { name: "เกิน 8–30 วัน", b: 2 }, { name: "เกินกว่า 30 วัน", b: 2 },
  { name: "ผลัดตั๋ว", b: 3 },
];
const NB = BINS.length;
const BIN_BUCKET = BINS.map((x) => x.b);
const binsOf = (j) => BINS.map((x, i) => (x.b === j ? i : -1)).filter((i) => i >= 0);
const TABLE_ROWS = [
  { name: "ก่อนวันครบกำหนด (รวม)", bins: binsOf(0), color: BUCKETS[0].color, cls: "subtotal" },
  ...AGES.map((m) => ({ name: `ตั๋วอายุ ${m} เดือน`, bins: [m * 2 - 1], cls: "sub" })),
  { name: "ตรงวันครบกำหนด", bins: binsOf(1), color: BUCKETS[1].color },
  { name: "เกินกำหนด (ไม่ได้ผลัด)", bins: binsOf(2), color: BUCKETS[2].color },
  { name: "เกินกำหนด (ผลัดตั๋ว)", bins: binsOf(3), color: BUCKETS[3].color },
];
const TH_M = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];

// ---------- formatting ----------
const fmt = (n) => Number(n || 0).toLocaleString("en-US");
const pct = (a, b) => (b ? (a / b) * 100 : 0);
const p2 = (x) => `${x.toFixed(2)}%`;
const be2 = (y) => String((+y + 543) % 100).padStart(2, "0");
const sum = (a) => a.reduce((x, y) => x + y, 0);
const sumBins = (arr, bins) => bins.reduce((x, i) => x + arr[i], 0);
function deltaPp(a, b) {
  const d = a - b;
  return Math.abs(d) < 0.005 ? "±0.00" : `${d > 0 ? "▲ +" : "▼ −"}${Math.abs(d).toFixed(2)}`;
}
const pctChange = (c) => `${c >= 0 ? "▲ +" : "▼ −"}${Math.abs(c).toFixed(2)}%`;
const toneOf = (pa, pb) => (pb === null || Math.abs(pa - pb) < 0.005 ? "flat" : "");
function thDate(s) {
  const [y, m, d] = s.split("-");
  return `${+d} ${TH_M[+m - 1]} ${be2(y)}`;
}

// ---------- state ----------
const loading = ref(false);
const errorMsg = ref("");
const data = ref({ firstDate: null, lastDate: null, total: 0, rows: [] });
const gran = ref("quarter");
const typeFilter = ref("all");
const measure = ref("pct");
const periodA = ref(null);
const periodB = ref(null);
const open = reactive({ timing: false, mix: false, dist: false });
const openYears = ref(new Set());
const showAll = ref(false); // all-periods table starts collapsed
const hover = ref(null);
const trendBox = ref(null);
const distBox = ref(null);
const trendWidth = ref(800);
const distWidth = ref(600);
const trendTouched = ref(false);

// ---------- periods ----------
const MONTHS = computed(() => [...new Set(data.value.rows.map((r) => r.month))].sort());
const hasData = computed(() => MONTHS.value.length > 0);
const firstMonth = computed(() => (data.value.firstDate || "").slice(0, 7));
const lastMonth = computed(() => (data.value.lastDate || "").slice(0, 7));
// first month in which tickets issued on the first data day could already have reached their due date
const warmEnd = computed(() => {
  if (!data.value.firstDate) return "";
  const [y, m, d] = data.value.firstDate.split("-").map(Number);
  const idx = y * 12 + (m - 1) + 6 + (d > 1 ? 1 : 0);
  return `${Math.floor(idx / 12)}-${String((idx % 12) + 1).padStart(2, "0")}`;
});
const warmText = computed(() => {
  if (!warmEnd.value) return "";
  const [y, m] = warmEnd.value.split("-");
  return `ข้อมูลเริ่ม ${thDate(data.value.firstDate)} ช่วงที่ใช้เทียบได้เต็มที่คือ ${TH_M[+m - 1]} ${be2(y)} เป็นต้นไป`;
});

function keyOf(month, g) {
  const [y, m] = month.split("-");
  if (g === "month") return month;
  if (g === "quarter") return `${y}-Q${Math.ceil(+m / 3)}`;
  return y;
}
function label(key, long) {
  if (!key) return "";
  if (/^\d{4}-\d{2}$/.test(key)) {
    const [y, m] = key.split("-");
    return `${TH_M[+m - 1]} ${long ? +y + 543 : be2(y)}`;
  }
  if (key.includes("Q")) {
    const [y, q] = key.split("-Q");
    return long ? `ไตรมาส ${q}/${+y + 543}` : `Q${q}/${be2(y)}`;
  }
  return `ปี ${+key + 543}`;
}
function monthsOf(key) {
  if (/^\d{4}-\d{2}$/.test(key)) return [key];
  if (key.includes("Q")) {
    const [y, q] = key.split("-Q");
    return [0, 1, 2].map((i) => `${y}-${String((q - 1) * 3 + 1 + i).padStart(2, "0")}`);
  }
  return Array.from({ length: 12 }, (_, i) => `${key}-${String(i + 1).padStart(2, "0")}`);
}
const periodList = computed(() => [...new Set(MONTHS.value.map((m) => keyOf(m, gran.value)))]);
const yearList = computed(() => [...new Set(periodList.value.map((k) => k.slice(0, 4)))]);

function coverage(key) {
  const ms = monthsOf(key);
  const present = ms.filter((m) => MONTHS.value.includes(m));
  const notes = [];
  if (present.includes(firstMonth.value)) notes.push(`ข้อมูลเริ่ม ${thDate(data.value.firstDate)}`);
  if (present.includes(lastMonth.value) && (present.length < ms.length || lastMonth.value === ms[ms.length - 1])) {
    notes.push(`ข้อมูลถึง ${thDate(data.value.lastDate)}`);
  } else if (present.length < ms.length) notes.push(`มีข้อมูล ${present.length} จาก ${ms.length} เดือน`);
  const warm = present.filter((m) => m < warmEnd.value).length;
  return { warmAll: present.length > 0 && warm === present.length, warmSome: warm > 0 && warm < present.length, notes };
}
const isWarm = (k) => coverage(k).warmAll;

// ---------- aggregation ----------
const blank = () => ({ n: Array.from({ length: 4 }, () => Array(NB).fill(0)), pv: Array.from({ length: 4 }, () => Array(NB).fill(0)) });
const agg = computed(() => {
  const out = {};
  for (const r of data.value.rows) {
    const t = API_TYPES.indexOf(r.type);
    if (t < 0 || r.bin < 0 || r.bin >= NB) continue;
    const k = keyOf(r.month, gran.value);
    const a = out[k] || (out[k] = blank());
    a.n[t][r.bin] += r.count;
    a.pv[t][r.bin] += r.principal;
  }
  return out;
});
const typeSel = computed(() => (typeFilter.value === "all" ? [0, 1, 2, 3] : [+typeFilter.value]));
function binsFor(key, types = typeSel.value) {
  const a = agg.value[key] || blank();
  const out = Array(NB).fill(0);
  for (const t of types) for (let b = 0; b < NB; b++) out[b] += a.n[t][b];
  return out;
}
function bucketsFor(key, types) {
  const out = [0, 0, 0, 0];
  binsFor(key, types).forEach((n, i) => { out[BIN_BUCKET[i]] += n; });
  return out;
}

function shiftPrev(key) {
  const i = periodList.value.indexOf(key);
  return i > 0 ? periodList.value[i - 1] : null;
}
function resetPeriods() {
  const list = periodList.value;
  periodA.value = list.length ? list[list.length - 1] : null;
  periodB.value = periodA.value ? shiftPrev(periodA.value) : null;
}
function setGran(g) {
  gran.value = g;
  resetPeriods();
  trendTouched.value = false;
}
function setA(k) {
  periodA.value = k;
  if (periodB.value === k) periodB.value = shiftPrev(k);
}
watch(periodA, (k) => { if (k && periodB.value === k) periodB.value = shiftPrev(k); });

// ---------- summary ----------
const selectionText = computed(() =>
  `${label(periodA.value, true)} · ${typeFilter.value === "all" ? "ทุกประเภทธุรกรรม" : TYPES[+typeFilter.value]}` +
  (periodB.value ? ` · เทียบ ${label(periodB.value, true)}` : " · ไม่เปรียบเทียบ"));

// Comparison text for the cards, in plain words: share cards show both periods' values and the
// straight difference (no "pp" jargon); the total card shows the relative change in count.
function changeWord(d, digits, unit) {
  if (Math.abs(d) < 0.005) return { text: "เท่าเดิม", dir: "flat" };
  return { text: `${d > 0 ? "▲ เพิ่มขึ้น" : "▼ ลดลง"} ${Math.abs(d).toFixed(digits)}${unit}`, dir: d > 0 ? "up" : "down" };
}
const tiles = computed(() => {
  if (!periodA.value) return [];
  const A = bucketsFor(periodA.value), nA = sum(A);
  const B = periodB.value ? bucketsFor(periodB.value) : null, nB = B ? sum(B) : 0;
  const pA = A.map((x) => pct(x, nA)), pB = B ? B.map((x) => pct(x, nB)) : null;
  const lb = periodB.value ? label(periodB.value) : "";
  const share = (a, b) => {
    if (b === null) return {};
    const c = changeWord(a - b, 2, "");
    return { cmp: `${lb}: ${p2(b)} → ${p2(a)}`, delta: c.text, dir: c.dir };
  };
  const total = (() => {
    if (!B || !nB) return {};
    const c = changeWord(((nA - nB) / nB) * 100, 2, "%");
    return { cmp: `${lb}: ${fmt(nB)} → ${fmt(nA)} รายการ`, delta: c.text, dir: c.dir };
  })();
  return [
    { label: "รายการทั้งหมด", icon: "receipt_long", accent: "#4f46e5", tint: "#eef2ff", value: fmt(nA), ...total },
    { label: "ก่อน due date", icon: "schedule", accent: BUCKETS[0].color, tint: "#eff6ff", value: p2(pA[0]), ...share(pA[0], pB ? pB[0] : null) },
    { label: "วันครบ due date", icon: "event_available", accent: BUCKETS[1].color, tint: "#f0fdfa", value: p2(pA[1]), ...share(pA[1], pB ? pB[1] : null) },
    {
      label: "เกิน due date", icon: "event_busy", accent: BUCKETS[2].color, tint: "#fffbeb", value: p2(pA[2] + pA[3]),
      split: `ไม่ได้ผลัด ${p2(pA[2])} · ผลัดตั๋ว ${p2(pA[3])}`, ...share(pA[2] + pA[3], pB ? pB[2] + pB[3] : null),
    },
  ];
});

// "how to read" box: the two kinds of comparison, worked through with the selected periods' numbers
const howTo = computed(() => {
  if (!periodA.value || !periodB.value) return null;
  const A = bucketsFor(periodA.value), nA = sum(A), B = bucketsFor(periodB.value), nB = sum(B);
  if (!nA || !nB) return null;
  const pa = pct(A[0], nA), pb = pct(B[0], nB), chg = ((nA - nB) / nB) * 100;
  const d = pa - pb;
  return {
    b: label(periodB.value, true),
    total: `${fmt(nB)} → ${fmt(nA)} รายการ คือ${chg >= 0 ? "มากขึ้น" : "น้อยลง"} ${Math.abs(chg).toFixed(2)}% ของจำนวนเดิม`,
    share: `ก่อน due ${p2(pb)} → ${p2(pa)} คือ${d >= 0 ? "เพิ่มขึ้น" : "ลดลง"} ${Math.abs(d).toFixed(2)} ` +
      `(${pb.toFixed(2)} ${d >= 0 ? "+" : "−"} ${Math.abs(d).toFixed(2)} = ${pa.toFixed(2)})`,
  };
});

const flags = computed(() => {
  const out = [];
  for (const [tag, k] of [["A", periodA.value], ["B", periodB.value]]) {
    if (!k) continue;
    const c = coverage(k);
    const bits = [...c.notes];
    if (c.warmAll) bits.push("อยู่ในช่วงตั้งต้นทั้งหมด ตั๋วส่วนใหญ่ยังไม่ครบกำหนด จึงแทบไม่มีรายการวันครบหรือเกิน due");
    else if (c.warmSome) bits.push("มีเดือนในช่วงตั้งต้นปนอยู่ สัดส่วนก่อน due จะสูงกว่าความจริง");
    if (bits.length) out.push({ tag, period: label(k), text: bits.join(" · ") });
  }
  return out;
});

const ledeHtml = computed(() => {
  if (!periodA.value) return "";
  const b = bucketsFor(periodA.value, [0, 1, 2, 3]), n = sum(b);
  return `${label(periodA.value, true)}: ลูกค้า <b>${p2(pct(b[0], n))}</b> มาทำรายการก่อน due date, <b>${p2(pct(b[1], n))}</b> มาวันครบ due และ ` +
    `<b>${p2(pct(b[2] + b[3], n))}</b> เกิน due (ในจำนวนนี้ ${p2(pct(b[3], n))} เป็นตั๋วที่ผลัดไว้)`;
});
const metaText = computed(() =>
  data.value.firstDate ? `${fmt(data.value.total)} รายการ · ${thDate(data.value.firstDate)} – ${thDate(data.value.lastDate)}` : "");

// ---------- trend chart ----------
function niceMax(v) {
  const p = 10 ** Math.floor(Math.log10(v || 1));
  for (const m of [1, 2, 2.5, 5, 10]) if (m * p >= v) return m * p;
  return 10 * p;
}
function roundedTop(x0, yb, w, h, r) {
  return r
    ? `M${x0},${yb} V${yb - h + r} Q${x0},${yb - h} ${x0 + r},${yb - h} H${x0 + w - r} Q${x0 + w},${yb - h} ${x0 + w},${yb - h + r} V${yb} Z`
    : `M${x0},${yb} V${yb - h} H${x0 + w} V${yb} Z`;
}
const trendTitle = computed(() =>
  `จังหวะการมาทำรายการ${gran.value === "month" ? "รายเดือน" : gran.value === "quarter" ? "รายไตรมาส" : "รายปี"} · ` +
  (typeFilter.value === "all" ? "ทุกธุรกรรม" : TYPES[+typeFilter.value]));
const trend = computed(() => {
  const list = periodList.value;
  const slot = gran.value === "month" ? 46 : gran.value === "quarter" ? 76 : 150;
  const L = 48, R = 12, Tp = 30, Hp = 300, Bt = 46;
  const W = Math.max(trendWidth.value, L + R + list.length * slot);
  const step = (W - L - R) / Math.max(list.length, 1);
  const bw = Math.min(gran.value === "month" ? 24 : 38, step * 0.5);
  const isPct = measure.value === "pct";
  const rows = list.map((k) => { const b = bucketsFor(k); return { k, b, n: sum(b), cov: coverage(k) }; });
  const ymax = isPct ? 100 : niceMax(Math.max(...rows.map((d) => d.n), 1));
  const y = (v) => Tp + Hp - (v / ymax) * Hp;
  const ticks = (isPct ? [0, 25, 50, 75, 100] : [0, 0.25, 0.5, 0.75, 1].map((f) => f * ymax)).map((v) => ({
    v, y: y(v), label: isPct ? `${v}%` : v >= 1000 ? `${(v / 1000).toLocaleString("en-US", { maximumFractionDigits: 1 })}k` : String(v),
  }));
  const bars = rows.map((d, i) => {
    const x0 = L + step * i, cx = x0 + step / 2, bx = cx - bw / 2;
    const vals = d.b.map((v, j) => ({ j, v: isPct ? pct(v, d.n) : v })).filter((s) => s.v > 0);
    let acc = 0;
    const segs = [];
    vals.forEach((s, idx) => {
      const yBot = y(acc), yTop = y(acc + s.v);
      acc += s.v;
      const gap = idx > 0 ? 2 : 0;
      const h = yBot - yTop - gap;
      if (h <= 0.4) return;
      const r = idx === vals.length - 1 ? Math.min(4, h / 2) : 0;
      segs.push({ j: s.j, d: roundedTop(bx, yBot - gap, bw, h, r), fill: BUCKETS[s.j].color });
    });
    const tag = d.k === periodA.value ? "A" : d.k === periodB.value ? "B" : null;
    const late = pct(d.b[2] + d.b[3], d.n);
    let l1, l2 = "";
    if (gran.value === "month") { const [yy, mm] = d.k.split("-"); l1 = TH_M[+mm - 1]; if (+mm === 1 || i === 0) l2 = String(+yy + 543); }
    else if (gran.value === "quarter") { const [yy, q] = d.k.split("-Q"); l1 = `Q${q}`; if (q === "1" || i === 0) l2 = String(+yy + 543); }
    else l1 = String(+d.k + 543);
    const tw = 250;
    return {
      k: d.k, n: d.n, x0, cx, segs, tag, warm: d.cov.warmAll, l1, l2,
      lateLabel: isPct && tag && step - bw >= 84 && late >= 0.005 ? `${late.toFixed(2)}%` : "",
      lateY: y(100 - late / 2) + 4,
      tipRows: d.b.map((v, j) => ({ name: BUCKETS[j].name, color: BUCKETS[j].color, n: v, pct: pct(v, d.n) })).reverse(),
      note: [...d.cov.notes, d.cov.warmAll ? "ช่วงตั้งต้น" : d.cov.warmSome ? "มีช่วงตั้งต้นปน" : ""].filter(Boolean).join(" · "),
      tipStyle: { left: `${Math.max(4, Math.min(W - tw - 4, cx + 18 > W - tw ? cx - tw - 18 : cx + 18))}px`, top: `${Tp + 10}px` },
    };
  });
  return { W, H: Tp + Hp + Bt, L, R, Tp, Hp, Bt, step, bw, ticks, bars };
});
const hoverBar = computed(() => (hover.value === null ? null : trend.value.bars[hover.value] || null));

// ---------- distribution chart ----------
const dist = computed(() => {
  const empty = { W: distWidth.value, H: 0, rows: [], grid: [], Tp: 6, rowH: 32, bh: 11 };
  if (!periodA.value) return empty;
  const a = binsFor(periodA.value), na = sum(a);
  const b = periodB.value ? binsFor(periodB.value) : null, nb = b ? sum(b) : 0;
  const rowsDef = open.dist
    ? BINS.map((x, i) => ({ name: x.name, b: x.b, bins: [i] }))
    : [{ name: "ก่อนวันครบกำหนด (รวม)", b: 0, bins: binsOf(0) }, ...BINS.map((x, i) => ({ name: x.name, b: x.b, bins: [i] })).filter((x) => x.b !== 0)];
  const W = Math.max(320, distWidth.value);
  const LW = Math.min(170, W * 0.36), R = 64, rowH = 32, bh = 11, Tp = 6;
  const H = Tp + rowsDef.length * rowH + 4;
  const vals = rowsDef.flatMap((r) => [pct(sumBins(a, r.bins), na), ...(b ? [pct(sumBins(b, r.bins), nb)] : [])]);
  const xmax = niceMax(Math.max(...vals, 1));
  const x = (v) => LW + (v / xmax) * (W - LW - R);
  const bar = (yy, w) => {
    if (w < 0.5) return "";
    const r = Math.min(4, bh / 2, w);
    return `M${LW},${yy} H${LW + w - r} Q${LW + w},${yy} ${LW + w},${yy + r} V${yy + bh - r} Q${LW + w},${yy + bh} ${LW + w - r},${yy + bh} H${LW} Z`;
  };
  const rows = rowsDef.map((r, i) => {
    const y0 = Tp + i * rowH;
    const va = pct(sumBins(a, r.bins), na), vb = b ? pct(sumBins(b, r.bins), nb) : null;
    return {
      name: r.name, y0, sep: i > 0 && rowsDef[i - 1].b !== r.b, color: BUCKETS[r.b].color,
      va, xA: x(va), dA: bar(y0 + 4, x(va) - LW),
      vb, xB: vb === null ? 0 : x(vb), dB: vb === null ? "" : bar(y0 + 4 + bh + 2, x(vb) - LW),
    };
  });
  return { W, H, rows, grid: [0, 0.5, 1].map((f) => x(f * xmax)), Tp, rowH, bh };
});

// ---------- comparison tables ----------
const timing = computed(() => {
  const cols = [...TYPES.map((n, i) => ({ n: `ธุรกรรม${n}`, types: [i] })), { n: "รวมทุกธุรกรรม", types: [0, 1, 2, 3] }];
  if (!periodA.value) return { cols, rows: [], totals: [] };
  const A = cols.map((c) => binsFor(periodA.value, c.types)), nA = A.map(sum);
  const B = periodB.value ? cols.map((c) => binsFor(periodB.value, c.types)) : null, nB = B ? B.map(sum) : null;
  const rows = TABLE_ROWS.map((r) => ({
    ...r,
    cells: cols.map((c, ci) => ({ pa: pct(sumBins(A[ci], r.bins), nA[ci]), pb: B ? pct(sumBins(B[ci], r.bins), nB[ci]) : null })),
  }));
  const totals = cols.map((c, ci) => ({
    nA: nA[ci], nB: B ? nB[ci] : null, chg: B && nB[ci] ? ((nA[ci] - nB[ci]) / nB[ci]) * 100 : null,
  }));
  return { cols, rows, totals };
});

const mix = computed(() => {
  if (!periodA.value) return [];
  const aA = agg.value[periodA.value] || blank(), aB = periodB.value ? agg.value[periodB.value] || blank() : null;
  const byBins = (a, bins) => {
    const n = [0, 0, 0, 0];
    let pv = 0;
    for (let t = 0; t < 4; t++) for (const bi of bins) { n[t] += a.n[t][bi]; pv += a.pv[t][bi]; }
    return { n, pv };
  };
  const all = BINS.map((_, i) => i);
  return [...TABLE_ROWS, { name: "รวมทุกกลุ่ม", bins: all, cls: "total" }].map((r) => {
    const RA = byBins(aA, r.bins), RB = aB ? byBins(aB, r.bins) : null;
    const nA = sum(RA.n), nB = RB ? sum(RB.n) : 0;
    return {
      ...r, nA,
      cells: RA.n.map((v, i) => ({ pa: pct(v, nA), pb: RB && nB ? pct(RB.n[i], nB) : null })),
      avgA: nA ? Math.round(RA.pv / nA) : null,
      avgB: nB ? Math.round(RB.pv / nB) : null,
    };
  });
});

// ---------- all periods (grouped by year, newest first) ----------
const allInfo = computed(() => {
  const unit = gran.value === "month" ? "เดือน" : gran.value === "quarter" ? "ไตรมาส" : "ปี";
  return gran.value === "year"
    ? `${periodList.value.length} ปี · ใหม่สุดอยู่บน`
    : `${periodList.value.length} ${unit} ใน ${yearList.value.length} ปี · ใหม่สุดอยู่บน · กดที่แถวปีเพื่อยืด/หด`;
});
function noteOf(k) {
  const c = coverage(k);
  return [...c.notes, c.warmAll ? "ช่วงตั้งต้น" : c.warmSome ? "มีช่วงตั้งต้นปน" : ""].filter(Boolean).join(" · ");
}
const tagsOf = (k) => [k === periodA.value ? "A" : null, k === periodB.value ? "B" : null].filter(Boolean);
const allRows = computed(() => {
  const list = periodList.value.slice().reverse();
  const unit = gran.value === "month" ? "เดือน" : "ไตรมาส";
  if (gran.value === "year") {
    return list.map((k) => { const b = bucketsFor(k); return { key: k, name: label(k, true), b, n: sum(b), note: noteOf(k), tags: tagsOf(k) }; });
  }
  const out = [];
  for (const y of yearList.value.slice().reverse()) {
    const keys = list.filter((k) => k.startsWith(y));
    const tot = [0, 0, 0, 0];
    keys.forEach((k) => bucketsFor(k).forEach((v, j) => { tot[j] += v; }));
    const collapsed = !openYears.value.has(y);
    out.push({
      key: `y${y}`, year: y, isYear: true, name: `ปี ${+y + 543}`, count: `${keys.length} ${unit}`, b: tot, n: sum(tot), note: "รวมทั้งปี",
      tags: collapsed ? [keys.includes(periodA.value) ? "A" : null, keys.includes(periodB.value) ? "B" : null].filter(Boolean) : [],
    });
    for (const k of keys) {
      const b = bucketsFor(k);
      out.push({ key: k, year: y, child: true, name: label(k, true).replace(/ \d{4}$/, "").replace(/\/\d{4}$/, ""), b, n: sum(b), note: noteOf(k), tags: tagsOf(k) });
    }
  }
  return out;
});
// opening the all-periods table always starts with every year collapsed
function toggleAll() {
  showAll.value = !showAll.value;
  if (showAll.value) openYears.value = new Set();
}
function toggleYear(y) {
  const s = new Set(openYears.value);
  if (s.has(y)) s.delete(y); else s.add(y);
  openYears.value = s;
}

// ---------- load + sizing ----------
async function loadData() {
  loading.value = true;
  errorMsg.value = "";
  try {
    const res = await store.fetchDueBehavior();
    data.value = { firstDate: res.firstDate, lastDate: res.lastDate, total: res.total || 0, rows: res.rows || [] };
    resetPeriods();
  } catch (e) {
    data.value = { firstDate: null, lastDate: null, total: 0, rows: [] };
    errorMsg.value = e?.message ? `โหลดข้อมูลไม่สำเร็จ: ${e.message}` : "โหลดข้อมูลไม่สำเร็จ";
  } finally {
    loading.value = false;
  }
}

let ro = null;
function measureBoxes() {
  if (trendBox.value) trendWidth.value = trendBox.value.clientWidth || trendWidth.value;
  if (distBox.value) distWidth.value = distBox.value.clientWidth || distWidth.value;
}
// keep the newest periods in view when the trend chart is wider than its card
watch(() => trend.value.W, async () => {
  await nextTick();
  const el = trendBox.value;
  if (el && !trendTouched.value && el.scrollWidth > el.clientWidth) el.scrollLeft = el.scrollWidth;
});
watch(hasData, async (v) => {
  if (!v) return;
  await nextTick();
  measureBoxes();
  if (ro) { ro.disconnect(); [trendBox.value, distBox.value].forEach((el) => el && ro.observe(el)); }
});

onMounted(() => {
  ro = new ResizeObserver(measureBoxes);
  loadData();
});
onBeforeUnmount(() => ro && ro.disconnect());
</script>

<style lang="scss" scoped>
.loading-state {
  position: fixed; top: 0; left: 0; width: 100%; height: 100%; background-color: rgba(0, 0, 0, 0.3);
  z-index: 9999; display: flex; justify-content: center; align-items: center;
}
.cb-page { color: #7b809a; }
.cb-controls { display: flex; flex-wrap: wrap; gap: 16px 28px; align-items: flex-start; }
.cb-ctl { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
.cb-ctl-wide { flex: 1 1 420px; max-width: 640px; }
.cb-seg { display: inline-flex; flex-wrap: wrap; gap: 6px; }
.cb-pair { display: flex; align-items: center; gap: 8px; }
.cb-tag {
  display: inline-grid; place-items: center; width: 24px; height: 24px; border-radius: 6px;
  font-size: 12px; font-weight: 700; flex-shrink: 0;
  &.a { background: #05af50; color: #fff; }
  &.b { background: #ced4da; color: #344767; }
  &.sm { width: 20px; height: 20px; font-size: 11px; margin-left: 6px; vertical-align: 1px; }
}
.cb-empty {
  display: flex; align-items: center; gap: 12px; padding: 16px; border-radius: 0.5rem; background: #f8f9fa; color: #344767;
  i { color: #05af50; font-size: 28px; }
}
.cb-section-head { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 8px; margin-bottom: 12px; h6 { color: #344767; } }
.cb-tiles { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; }
@media (max-width: 991px) { .cb-tiles { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
/* stat-tile pattern from views/generals/dashboards.vue */
.stat-tile {
  background: #f8f9fa; border-radius: 12px; padding: 14px 10px; border-top: 3px solid;
  display: grid; grid-template-columns: auto auto; grid-template-areas: "label label" "ico value" "delta delta";
  justify-content: center; justify-items: center; align-items: center; gap: 6px 10px; text-align: center; min-width: 0;
}
.stat-label { grid-area: label; color: #67748e; font-size: 15px; font-weight: 600; }
/* overflow hidden: an icon name missing from the app's Material Icons Round font would otherwise spill out as text */
.stat-ico { grid-area: ico; width: 38px; height: 38px; border-radius: 10px; display: grid; place-items: center; overflow: hidden; i { font-size: 20px; } }
.stat-value { grid-area: value; color: #344767; font-size: 30px; font-weight: 700; line-height: 1.3; }
.stat-delta {
  grid-area: delta; display: flex; flex-direction: column; gap: 3px; font-size: 12px; color: #7b809a;
  .split { color: #67748e; }
  .chg { font-size: 13px; font-weight: 700; color: #344767; &.flat { color: #7b809a; font-weight: 400; } }
}
.cb-howto {
  display: flex; gap: 10px; align-items: flex-start; margin-top: 12px; padding: 12px 16px; border-radius: 8px;
  background: #f8f9fa; border-left: 3px solid #4f46e5; color: #67748e; font-size: 13px;
  > i { color: #4f46e5; font-size: 20px; }
  ul { margin: 0; padding-left: 18px; }
  li { margin: 2px 0; }
  b { color: #344767; }
}
.cb-howto-title { margin: 0 0 4px; color: #344767; font-weight: 700; font-size: 14px; }
.cb-flag {
  display: flex; gap: 8px; align-items: flex-start; margin-top: 12px; padding: 12px 16px; border-radius: 8px;
  background: #f8f9fa; border-left: 3px solid #fb8c00; color: #67748e; font-size: 13px;
  i { color: #fb8c00; font-size: 20px; }
  b { color: #344767; }
}
.cb-insight {
  display: flex; gap: 12px; align-items: flex-start; margin-top: 12px; padding: 14px 16px; border-radius: 8px; background: #e8fef2;
  > i { color: #05af50; font-size: 22px; }
  :deep(b) { color: #344767; }
}
.cb-insight-label { color: #05602d; font-size: 14px; font-weight: 700; margin-bottom: 2px; }
.cb-card-head { display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 10px 20px; h6 { color: #344767; } }
.cb-legend { display: flex; flex-wrap: wrap; gap: 6px 16px; font-size: 13px; color: #67748e; margin-bottom: 8px; > span { display: inline-flex; align-items: center; gap: 6px; } }
.cb-sw { width: 10px; height: 10px; border-radius: 50%; display: inline-block; flex-shrink: 0; }
.cb-chart-scroll { overflow-x: auto; position: relative; scrollbar-width: thin; }
.cb-chart { position: relative; svg { display: block; } }
.cb-tip {
  position: absolute; z-index: 5; min-width: 220px; pointer-events: none; padding: 12px; background: #fff; color: #344767;
  border: 1px solid #dee2e6; border-radius: 8px; box-shadow: 0 8px 24px rgba(52, 71, 103, 0.18); font-size: 13px; line-height: 1.6;
}
.cb-tip-title { font-weight: 700; margin-bottom: 6px; }
.cb-tip-row { display: flex; align-items: center; gap: 8px; }
.cb-tip-v { margin-left: auto; padding-left: 12px; }
.cb-footnote { margin: 12px 0 0; padding-top: 10px; border-top: 1px solid #dee2e6; font-size: 12px; }
.cb-toggle-btn { display: inline-flex; align-items: center; gap: 4px; i { font-size: 18px; } }
.cb-badge { padding: 4px 10px; border-radius: 6px; background: #e8fef2; color: #05602d; font-size: 12px; font-weight: 600; white-space: nowrap; }
.cb-table-context {
  display: flex; flex-wrap: wrap; align-items: center; gap: 8px 16px; padding: 10px 14px; margin-bottom: 0;
  background: #f8f9fa; border: 1px solid #dee2e6; border-bottom: 0; border-radius: 10px 10px 0 0; font-size: 13px; color: #67748e;
  > span { display: inline-flex; align-items: center; gap: 7px; }
  b { color: #344767; }
}

/* comparison tables — header colour comes from the app's table.table thead (#194D30) */
.cb-cmp-scroll { border: 1px solid #dee2e6; border-radius: 0 0 10px 10px; }
.cb-cmp { border-collapse: separate; border-spacing: 0; font-size: 0.875rem; color: #344767;
  th, td { padding: 9px 8px; border-bottom: 1px solid #dee2e6; border-right: 1px solid #dee2e6; text-align: right; white-space: nowrap; }
  th:last-child, td:last-child { border-right: 0; }
  thead th { color: #fff; text-align: center; vertical-align: middle; font-weight: 600; }
  thead tr.grp th { border-bottom: 1px solid rgba(255, 255, 255, 0.28); }
  thead tr.subhead th { background: #05602d; padding: 8px 10px 10px !important; line-height: 1.25; }
  .th-main { display: block; font-weight: 600; }
  .th-sub { display: block; margin-top: 2px; font-size: 11px; font-weight: 400; opacity: 0.8; }
  .group-start { border-left: 2px solid #dee2e6; }
  thead .group-start { border-left-color: rgba(255, 255, 255, 0.5); }
  /* only the row-label header (rowspan 2) and the row labels stay put while scrolling sideways */
  thead tr.grp th.cb-rowhead { position: sticky; left: 0; z-index: 4; background: #194d30; text-align: left; box-shadow: 2px 0 0 #dee2e6; }
  tbody th { position: sticky; left: 0; z-index: 2; background: #fff; color: #344767; text-align: left; font-weight: 600; box-shadow: 2px 0 0 #dee2e6; }
  tbody tr:hover td, tbody tr:hover th { background: #e8fef2; }
  tbody tr.subtotal { cursor: pointer; td, th { background: #eff6ff; font-weight: 700; } }
  tbody tr.sub th { padding-left: 30px; font-weight: 400; color: #67748e; }
  tbody tr.sub td { font-size: 13px; }
  tbody tr.total td, tbody tr.total th { background: #90f9be; font-weight: 700; }
  td.b { color: #7b809a; }
  td.d { color: #344767; font-weight: 600; font-size: 13px; &.flat { color: #7b809a; font-weight: 400; } }
}
.cb-rowlabel { display: inline-flex; align-items: center; gap: 6px; }
.cb-row-toggle {
  display: inline-grid; place-items: center; width: 24px; height: 24px; padding: 0; border: 1px solid #05af50; border-radius: 6px;
  background: #fff; color: #05af50; cursor: pointer; vertical-align: middle;
  i { font-size: 18px; transition: transform 0.15s; }
  &[aria-expanded="true"] i { transform: rotate(90deg); }
  &:hover { background: #05af50; color: #fff; }
}
.cb-section-toggle {
  display: flex; align-items: center; gap: 10px; padding: 0; border: 0; background: transparent; text-align: left; cursor: pointer; color: inherit;
  h6 { color: #344767; }
  &[aria-expanded="true"] .cb-row-toggle i { transform: rotate(90deg); }
  &:hover .cb-row-toggle { background: #05af50; color: #fff; }
}
.cb-row-hint { margin-left: 4px; font-size: 12px; font-weight: 400; color: #7b809a; }
.cb-cellbar { display: inline-flex; align-items: center; gap: 8px; i { display: inline-block; height: 6px; border-radius: 3px; background: #05af50; opacity: 0.45; } }

/* all-periods table */
.cb-all-scroll { max-height: 620px; overflow: auto; border: 1px solid #dee2e6; border-radius: 10px; }
.cb-all { min-width: 900px; font-size: 0.875rem; color: #344767;
  th, td { padding: 8px; border-bottom: 1px solid #dee2e6; text-align: right; white-space: nowrap; vertical-align: middle; }
  thead th { position: sticky; top: 0; z-index: 3; background: #194d30; color: #fff; text-align: center; }
  thead th:first-child { text-align: left; }
  tbody th { text-align: left; font-weight: 600; color: #344767; }
  td small { display: block; font-size: 11px; color: #7b809a; line-height: 1.2; }
  td.note { text-align: left; white-space: normal; min-width: 160px; font-size: 12px; color: #7b809a; }
  tr.year-row th, tr.year-row td { background: #e3feef; font-weight: 700; cursor: pointer; }
  tr.year-row:hover th, tr.year-row:hover td { background: #d1fee4; }
  tr.child th { padding-left: 38px; font-weight: 400; }
  tr.is-a th, tr.is-a td { background: #e8fef2; }
  .cb-row-toggle { margin-right: 6px; }
}
.cb-mixbar { display: flex; gap: 2px; width: 150px; height: 10px; margin-left: auto; span { height: 100%; border-radius: 2px; min-width: 0; } }
.cb-notes { margin: 0; padding-left: 20px; display: grid; gap: 8px; b { color: #344767; } }
</style>
